import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Calendar, Clock, Phone } from "lucide-react";
import {
  T, FONT_DISPLAY, FONT_MONO, PageShell, TopNav, Footer,
  container, eyebrow, h2, italicAccent,
} from "@/lib/hybridTheme";
import PublicChatWidget from "@/components/PublicChatWidget";
import api from "@/lib/api";
import { useState } from "react";

const CALENDLY_URL = "https://calendly.com/clientsonramp-sales";

export default function BookACall() {
  const [agent, setAgent] = useState(null);
  useEffect(() => {
    api.get("/public/site-config").then(({ data }) => setAgent(data.agent || null)).catch(() => {});
    // Load Calendly inline widget script
    const existing = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
    if (!existing) {
      const s = document.createElement("script");
      s.src = "https://assets.calendly.com/assets/external/widget.js";
      s.async = true;
      document.head.appendChild(s);
    }
    // Load stylesheet
    const linkExisting = document.querySelector('link[href="https://assets.calendly.com/assets/external/widget.css"]');
    if (!linkExisting) {
      const l = document.createElement("link");
      l.href = "https://assets.calendly.com/assets/external/widget.css";
      l.rel = "stylesheet";
      document.head.appendChild(l);
    }
  }, []);

  return (
    <PageShell>
      <TopNav activeKey="book" />

      {/* HERO */}
      <section style={{ padding: "88px 0 40px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -80, right: -100, width: 500, height: 500, background: "radial-gradient(circle, rgba(229,83,59,0.10) 0%, transparent 60%)", pointerEvents: "none", filter: "blur(60px)" }} />
        <div style={container()}>
          <div style={{ maxWidth: 760, position: "relative" }}>
            <div style={eyebrow()}>Book a call</div>
            <h1 style={h2()}>
              Grab 20 minutes — <em style={italicAccent(T.brass)}>we'll show you your free site live.</em>
            </h1>
            <p style={{ fontSize: 17, marginTop: 20, color: T.inkMuted, lineHeight: 1.6, maxWidth: 640 }}>
              Pick a time that works. On the call we'll build your free preview website in real time, walk through pricing, and answer every question. No slides. No pressure. Zero prep needed on your end.
            </p>

            <div style={{ marginTop: 24, display: "flex", flexWrap: "wrap", gap: 20, fontSize: 13, color: T.inkFaint }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Clock size={14} color={T.brass} /> 20 minutes</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Phone size={14} color={T.coral} /> Video or phone — you choose</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CheckCircle2 size={14} color={T.sage} /> Free preview built live</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Calendar size={14} color={T.brass} /> Auto-syncs to your calendar</span>
            </div>
          </div>
        </div>
      </section>

      {/* CALENDLY INLINE EMBED */}
      <section style={{ padding: "20px 0 80px" }}>
        <div style={container()}>
          <div
            className="calendly-inline-widget"
            data-url={`${CALENDLY_URL}?hide_gdpr_banner=1&primary_color=e5533b&text_color=0a192f&background_color=f7f4ee`}
            style={{ minWidth: 320, height: 780, border: `1px solid ${T.lineSoft}`, background: T.canvas }}
            data-testid="calendly-widget"
          />
          <p style={{ fontSize: 12, color: T.inkFaint, textAlign: "center", marginTop: 16, fontFamily: FONT_MONO, letterSpacing: "0.06em" }}>
            Prefer to book direct?{" "}
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: T.coral, textDecoration: "underline" }}
              data-testid="calendly-direct-link"
            >
              Open Calendly in a new tab
            </a>
          </p>
        </div>
      </section>

      {/* WHAT TO EXPECT — reassures they're not walking into a hard sell */}
      <section style={{ padding: "80px 0", background: T.canvas, borderTop: `1px solid ${T.lineSoft}` }}>
        <div style={container()}>
          <div style={{ maxWidth: 760, marginBottom: 44 }}>
            <div style={eyebrow()}>What to expect</div>
            <h2 style={h2()}>
              A working demo, not a pitch deck — <em style={italicAccent(T.brass)}>you'll see the real thing.</em>
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
            {[
              { n: "01", h: "Discovery (5 min)", d: "Tell us about your business, what's leaking revenue, and what a good week looks like." },
              { n: "02", h: "Build (10 min)", d: "We generate your free preview website live and configure the AI receptionist on your line." },
              { n: "03", h: "Decide (5 min)", d: "Try the AI, keep the site free forever, and pick a plan only if it makes sense. No pressure." },
            ].map((s) => (
              <div key={s.n} style={{ background: T.panel, border: `1px solid ${T.line}`, padding: "28px 26px" }}>
                <div style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: "0.12em", color: T.coral, fontWeight: 700 }}>{s.n}</div>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 500, marginTop: 12, color: T.ink }}>{s.h}</h3>
                <p style={{ fontSize: 14, marginTop: 10, color: T.inkMuted, lineHeight: 1.6 }}>{s.d}</p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 40, textAlign: "center", fontSize: 13, color: T.inkFaint }}>
            Not ready to book?{" "}
            <Link to="/" style={{ color: T.brass, textDecoration: "underline" }} data-testid="book-back-home">
              Poke around the site first
            </Link>{" "}
            or ping the AI chat in the bottom-right — it&apos;s a live demo of what we&apos;ll install on your line.
          </div>
        </div>
      </section>

      <Footer />
      <PublicChatWidget agent={agent} />
    </PageShell>
  );
}
